import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ClientIntakeForm } from "@/components/clinician/ClientIntakeForm";
import { Badge } from "@/components/ui/badge";
import { FileText, CheckCircle, Clock, Edit } from "lucide-react";

interface ClientIntakeFormViewProps {
  clientId: string;
  clinicianId: string;
}

interface IntakeForm {
  id: string;
  status: string;
  submitted_at: string | null;
  reviewed_at: string | null;
  clinician_notes: string | null;
}

export function ClientIntakeFormView({ clientId, clinicianId }: ClientIntakeFormViewProps) {
  const [form, setForm] = useState<IntakeForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    loadForm();
  }, [clientId, clinicianId]);

  const loadForm = async () => {
    const { data, error } = await supabase
      .from('client_intake_forms')
      .select('id, status, submitted_at, reviewed_at, clinician_notes')
      .eq('client_id', clientId)
      .eq('clinician_id', clinicianId)
      .maybeSingle();

    if (error) {
      console.error('Error loading intake form:', error);
    }
    setForm(data);
    setLoading(false);
  };

  const handleFormComplete = () => {
    setShowForm(false);
    loadForm();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (showForm) {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => setShowForm(false)}>
          ← Back
        </Button>
        <ClientIntakeForm
          clinicianId={clinicianId}
          onClose={handleFormComplete}
        />
      </div>
    );
  }

  const getStatusInfo = () => {
    if (!form) {
      return {
        icon: FileText,
        label: 'Not Started',
        color: 'bg-muted text-muted-foreground',
        description: 'Your intake form helps your practitioner understand your needs better.'
      };
    }
    
    switch (form.status) {
      case 'submitted':
        return {
          icon: Clock,
          label: 'Pending Review',
          color: 'bg-yellow-500/10 text-yellow-500',
          description: 'Your form has been submitted and is awaiting review.'
        };
      case 'reviewed':
        return {
          icon: CheckCircle,
          label: 'Reviewed',
          color: 'bg-green-500/10 text-green-500',
          description: 'Your practitioner has reviewed your intake form.'
        };
      default:
        return {
          icon: Edit,
          label: 'Draft',
          color: 'bg-blue-500/10 text-blue-500',
          description: 'Your form is saved as a draft. Submit when ready.'
        };
    }
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Intake Form
        </CardTitle>
        <CardDescription>
          Share important information about yourself with your practitioner
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-start gap-4 p-4 bg-muted/30 rounded-lg">
          <StatusIcon className="h-8 w-8 text-muted-foreground mt-1" />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
            </div>
            <p className="text-muted-foreground">{statusInfo.description}</p>
            {form?.clinician_notes && (
              <div className="mt-4 p-3 bg-background rounded border">
                <p className="text-sm font-medium mb-1">Notes from your practitioner:</p>
                <p className="text-sm text-muted-foreground">{form.clinician_notes}</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3">
          {!form ? (
            <Button onClick={() => setShowForm(true)} className="gap-2">
              <FileText className="h-4 w-4" />
              Start Intake Form
            </Button>
          ) : form.status === 'draft' ? (
            <Button onClick={() => setShowForm(true)} className="gap-2">
              <Edit className="h-4 w-4" />
              Continue Form
            </Button>
          ) : (
            <Button variant="outline" onClick={() => setShowForm(true)} className="gap-2">
              <Edit className="h-4 w-4" />
              View / Update Form
            </Button>
          )}
        </div>

        <div className="border-t pt-4">
          <h4 className="font-medium mb-2">What's included in the intake form?</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Physical health and wellness information</li>
            <li>• Emotional state and coping strategies</li>
            <li>• Mental health history and current concerns</li>
            <li>• Spiritual beliefs and practices (if applicable)</li>
            <li>• Relationship dynamics and support network</li>
            <li>• Therapy goals and expectations</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
